import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { GlobalConst } from '@app/core/constants/global.const';
import { DeviceDetectService, NavDataService } from '@app/core/services';
import { AuthSessionService } from '@app/core/services/auth-session/auth-session.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'iko-iwo-selector',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './iko-iwo-selector.component.html',
  styleUrl: './iko-iwo-selector.component.scss',
})
export class IkoIwoSelectorComponent implements OnInit, OnDestroy {
  @ViewChild('logoDropdown') protected logoDropdown?: ElementRef;
  protected readonly isLogged = signal(false);
  protected readonly logoDropdownOpened = signal(false);
  protected readonly deviceDetectService: DeviceDetectService = inject(DeviceDetectService);
  protected readonly navDataService: NavDataService = inject(NavDataService);
  private readonly authSessionService: AuthSessionService = inject(AuthSessionService);
  private readonly zone: NgZone = inject(NgZone);
  private readonly iwoUrl = GlobalConst.iwoUrl;
  private readonly unsubscribeAll: Subject<void> = new Subject<void>();

  @HostListener('document:click', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  @HostListener('document:mouseover', ['$event'])
  protected checkExternalUserInteraction(event: Event) {
    this.zone.run(() => {
      if (!this.logoDropdown?.nativeElement.contains(event.target)) {
        this.logoDropdownOpened.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.authSessionService.currentUserProfile$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(() => this.isLogged.set(this.authSessionService.isLogged));
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }

  /**
   * Handles touch events on mobile devices for the logo dropdown.
   * Toggles the state of `logoDropdownOpened`. If the dropdown is not opened after toggling,
   * it triggers navigation to the home page by calling `openHome()`.
   *
   * @protected
   */
  protected handleMobileTouch() {
    this.zone.run(() => {
      if (this.logoDropdownOpened()) {
        this.openHome();
      }
      this.logoDropdownOpened.update((opened) => !opened);
    });
  }

  /**
   * Handles click events on desktop devices.
   *
   * This method checks if the current device is a tablet or mobile using the `deviceDetectService`.
   * If so, it exits early and does nothing. Otherwise, if the logo dropdown is open,
   * it triggers navigation to the home page by calling `openHome()`.
   *
   * @protected
   */
  protected handleDesktopClick() {
    if (this.deviceDetectService.isTablet || this.deviceDetectService.isMobile()) {
      return;
    }
    this.zone.run(() => {
      if (this.logoDropdownOpened()) {
        this.openHome();
      }
    });
  }

  protected handleMouseEnter() {
    if (this.deviceDetectService.isTablet || this.deviceDetectService.isMobile()) {
      return;
    }
    this.zone.run(() => {
      this.logoDropdownOpened.set(true);
    });
  }

  /**
   * Handles the click event to open the IWO (presumably a specific section or page).
   *
   * - Prevents the default action and stops event propagation.
   * - Navigates to the URL specified by `this.iwoUrl` using the `navDataService`.
   * - Closes the logo dropdown by setting `logoDropdownOpened` to false.
   *
   * @param $event - The event object from the click or interaction triggering this method.
   */
  protected openIWO($event: any): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.navDataService.navigate(this.iwoUrl, true);
    this.logoDropdownOpened.set(false);
  }

  /**
   * Navigates the user to the appropriate home page based on their authentication status.
   * If the user is logged in, navigates to the '/account' route; otherwise, navigates to the root '/' route.
   * Also closes the logo dropdown menu after navigation.
   *
   * @private
   */
  private openHome() {
    this.navDataService.navigate(this.authSessionService.isLogged ? '/account' : '/');
    setTimeout(() => this.logoDropdownOpened.set(false), 5);
  }
}
